"use client"

import { iconsMap } from "@/public/icons/iconsMap"
import CircleImageLinkListTable from "@/ui/CircleImageLinkListTable/CircleImageLinkListTable"
import { Desktop, Mobile } from "@/ui/common/bit/MediaQueries"
import { extractBlockProperties } from "@/utils/simplifyData"
import { yupResolver } from "@hookform/resolvers/yup"
import { CircularProgress } from "@mui/material"
import { FormFieldDto } from "api"
import axios from "axios"
import { useTranslations } from "next-intl"
import { usePathname } from "next/navigation"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import * as yup from "yup"
import BlockWrapper from "../../common/BlockWrapper"
import FindingSupplierSelectField from "./FindingSupplierSelectField"
import {
  FormFieldsWrapper,
  StyledButton,
  StyledContainer,
  StyledFormContainer,
  StyledFormTitles,
  StyledPaginationButton,
  StyledPaginationContainer,
  StyledPaginationInfo,
  StyledResultsNumber,
  StyledSearchResultsContainer,
} from "./styles"
import {
  AvailableFilters,
  FindingSupplierProps,
  FormState,
  FormValues,
  PaginationState,
  SupplierResult,
} from "./types"

const FindingSupplier = ({ data, id }: FindingSupplierProps) => {
  const t = useTranslations("FindingSupplier")
  const { title, itemsList, preTextResult, postTextResult } = data
  const titlesArray = extractBlockProperties(itemsList?.blocks)
  const pathname = usePathname()

  const [formState, setFormState] = useState<FormState>("idle")
  const [results, setResults] = useState<SupplierResult[]>([])
  const [filters, setFilters] = useState<AvailableFilters>({
    names: [],
    cities: [],
    districts: [],
  })
  const [isLoadingFilters, setIsLoadingFilters] = useState(false)
  const [isLoadingResults, setIsLoadingResults] = useState(false)
  const [serviceType, setServiceType] = useState<string>("")
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [searchParams, setSearchParams] = useState<Record<string, any>>({})

  // Pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 0,
    totalPages: 0,
    totalCount: 0,
    pageSize: 10,
  })

  // Icon mapping
  const SearchIcon = iconsMap["search"]
  const LeftArrowIcon = iconsMap["arrow_lightPink_black_line"]
  const RightArrowIcon = iconsMap["arrow_lightPink_black_line"]

  // Extract service type from URL
  useEffect(() => {
    if (pathname) {
      let extractedType = "garages" // Default
      const serviceTypes = ["garages", "appraiser", "agents"]

      serviceTypes.forEach((type) => {
        if (pathname.includes(type)) {
          if (type === "appraiser") {
            extractedType = "assessors"
          } else {
            extractedType = type
          }
        }
      })
      setServiceType(extractedType)
    }
  }, [pathname])

  // Fetch available filters on component mount
  useEffect(() => {
    const fetchFilters = async () => {
      if (!serviceType) return

      try {
        setIsLoadingFilters(true)
        const response = await axios.post(
          `/api/${serviceType}/${serviceType}-api`,
          {
            requestPage: 0,
            pageSize: 100,
          },
        )
        if (response?.data?.body?.availableFilters) {
          setFilters(response?.data?.body?.availableFilters)
        }
      } catch (error) {
        console.error(`Error fetching ${serviceType} filters:`, error)
      } finally {
        setIsLoadingFilters(false)
      }
    }

    if (serviceType) {
      fetchFilters()
    }
  }, [serviceType])

  // Form validation schema - all fields are optional now
  const schema = yup.object().shape({
    name: yup.string(),
    city: yup.string(),
    district: yup.string(),
  })

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
    getValues,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      city: "",
      district: "",
    },
  })

  // Function to fetch filtered options when district changes
  const fetchFilteredOptions = async (district: string) => {
    if (!district || !serviceType) return

    try {
      setIsLoadingFilters(true)
      const response = await axios.post(
        `/api/${serviceType}/${serviceType}-api`,
        {
          district: district,
          requestPage: 0,
          pageSize: 100,
        },
      )

      if (response?.data?.body?.availableFilters) {
        const { cities = [], names = [] } = response.data.body.availableFilters
        setFilters((prevFilters) => ({
          ...prevFilters,
          cities,
          names,
        }))

        // Reset city and name values since the options have changed
        setValue("city", "")
        setValue("name", "")

        // Clear results when district filter changes
        if (formState === "results") {
          setFormState("idle")
          setResults([])
          setPagination({
            currentPage: 0,
            totalPages: 0,
            totalCount: 0,
            pageSize: pagination.pageSize,
          })
        }
      }
    } catch (error) {
      console.error(`Error fetching filtered ${serviceType} options:`, error)
    } finally {
      setIsLoadingFilters(false)
    }
  }

  // Function to fetch filtered names when city changes
  const fetchFilteredNamesByCity = async (district: string, city: string) => {
    if (!district || !city || !serviceType) return

    try {
      setIsLoadingFilters(true)
      const response = await axios.post(
        `/api/${serviceType}/${serviceType}-api`,
        {
          district: district,
          city: city,
          requestPage: 0,
          pageSize: 100,
        },
      )

      if (response?.data?.body?.availableFilters) {
        const { names = [] } = response.data.body.availableFilters
        setFilters((prevFilters) => ({
          ...prevFilters,
          names,
        }))

        // Reset name value since the options have changed
        setValue("name", "")

        // Clear results when city filter changes
        if (formState === "results") {
          setFormState("idle")
          setResults([])
          setPagination({
            currentPage: 0,
            totalPages: 0,
            totalCount: 0,
            pageSize: pagination.pageSize,
          })
        }
      }
    } catch (error) {
      console.error(
        `Error fetching filtered ${serviceType} names by city:`,
        error,
      )
      // On error, don't change the names filter - keep existing names
    } finally {
      setIsLoadingFilters(false)
    }
  }

  // Watch for district changes to trigger filter updates
  const selectedDistrict = watch("district")
  const selectedCity = watch("city")
  const selectedName = watch("name")

  // Clear results when name changes
  useEffect(() => {
    if (isSubmitted && formState === "results" && selectedName) {
      setFormState("idle")
      setResults([])
      setPagination({
        currentPage: 0,
        totalPages: 0,
        totalCount: 0,
        pageSize: pagination.pageSize,
      })
    }
  }, [selectedName, isSubmitted])

  useEffect(() => {
    if (selectedDistrict && serviceType) {
      fetchFilteredOptions(selectedDistrict)
    }
  }, [selectedDistrict, serviceType])

  // Add effect to handle city changes
  useEffect(() => {
    if (selectedDistrict && selectedCity && serviceType) {
      fetchFilteredNamesByCity(selectedDistrict, selectedCity)
    } else if (selectedDistrict && !selectedCity && serviceType) {
      // Reset name when city is cleared but district remains
      setValue("name", "")
      setFilters((prevFilters) => ({
        ...prevFilters,
        names: [],
      }))
    }
  }, [selectedCity, selectedDistrict, serviceType, setValue])

  // Function to fetch search results with pagination
  const fetchResults = async (page: number, params: Record<string, any>) => {
    if (!serviceType) return

    try {
      setIsLoadingResults(true)

      const requestParams = {
        ...params,
        requestPage: page,
        pageSize: pagination.pageSize,
      }
      const response = await axios.post(
        `/api/${serviceType}/${serviceType}-api`,
        requestParams,
      )
      // Adjust the result extraction based on the expected response structure
      const resultItems =
        response?.data?.body?.[`${serviceType}`]?.items ||
        response.data.body.items ||
        []

      const totalCount =
        response?.data?.body?.[`${serviceType}`]?.totalCount ||
        response?.data?.body?.totalCount ||
        resultItems.length

      // Calculate total pages
      const totalPages = Math.ceil(totalCount / pagination.pageSize)
      // Transform the API response items to have the required structure
      const parsedResultItems = resultItems.map((item: any) => {
        // Ensure we have a column object for each result
        const baseColumns: any[] = [
          { marketingName: item?.marketingName || item?.contactName },
          {
            address: {
              fullAddress: item?.address,
              location: item?.location,
              city: item?.city,
              district: item?.district,
            },
          },
          {
            contactInfo: {
              phoneNumber: item?.phoneNumber || item?.phone,
              emailAddress: item?.emailAddress || item?.eMail,
            },
          },
        ]

        // Add service-specific columns
        if (serviceType === "garages") {
          baseColumns.push({ contactName: item?.contactName })
        } else if (serviceType === "assessors") {
          baseColumns.push({ specialty: item?.specialty })
        }
        return {
          columns: baseColumns,
        }
      })

      // Update state with new results and pagination information
      setResults(parsedResultItems)
      setPagination({
        currentPage: page,
        totalPages: totalPages,
        totalCount: totalCount,
        pageSize: pagination.pageSize,
      })

      setFormState("results")
    } catch (error) {
      console.error(`Error searching ${serviceType} suppliers:`, error)
      setFormState("idle")
    } finally {
      setIsLoadingResults(false)
    }
  }

  // Handle form submission
  const onSubmit = async (formData: FormValues) => {
    setIsSubmitted(true)
    if (!serviceType) return

    // Build request parameters - using directly from form data
    const requestParams: Record<string, any> = {
      requestPage: 0,
      pageSize: pagination.pageSize,
    }

    // Get the latest form values to ensure we have the most up-to-date data
    const currentFormData = getValues()

    if (currentFormData.name) requestParams.marketingName = currentFormData.name
    if (currentFormData.city) requestParams.city = currentFormData.city
    if (currentFormData.district)
      requestParams.district = currentFormData.district

    // Start loading and set search parameters immediately
    setFormState("loading")
    setSearchParams(requestParams)

    try {
      // Fetch results with the constructed parameters
      await fetchResults(0, requestParams)
    } catch (error) {
      console.error(`Error searching ${serviceType} suppliers:`, error)
      setFormState("idle")
    }
  }

  // Handle pagination navigation
  const handlePageChange = (newPage: number) => {
    if (newPage < 0 || newPage >= pagination.totalPages) return
    fetchResults(newPage, searchParams)
  }

  // Convert filters to the format expected by the SelectField component
  const createFieldConfig = (
    name: string,
    label: string,
    options: string[],
  ): FormFieldDto => {
    return {
      id: name,
      helpText: label,
      caption: label,
      required: false,
      type: {
        id: `dropdown-${name}`, // Generate a unique ID for each field type
        name: "Dropdown",
        supportsPreValues: true,
        supportsUploadTypes: false,
        renderInputType: "Multiple",
      },
      preValues: options.map((option) => ({ value: option, caption: option })),
    }
  }

  const districtField =
    filters.districts && filters.districts.length > 0
      ? createFieldConfig("district", t("byDistrict"), filters.districts || [])
      : undefined
  const cityField =
    filters.cities && filters.cities.length > 0
      ? createFieldConfig("city", t("byCity"), filters.cities || [])
      : undefined
  const nameField =
    filters.names && filters.names.length > 0
      ? createFieldConfig("name", t("byName"), filters.names || [])
      : undefined

  return (
    <BlockWrapper id={id}>
      <StyledContainer>
        {title && <StyledFormTitles title={title?.value} />}
        <StyledFormContainer>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {formState !== "loading" && (
              <FormFieldsWrapper>
                {districtField && (
                  <FindingSupplierSelectField
                    field={districtField || undefined}
                    control={control}
                    error={errors.district}
                    isSubmitted={isSubmitted}
                  />
                )}
                {cityField && (
                  <FindingSupplierSelectField
                    field={cityField}
                    control={control}
                    error={errors.city}
                    isSubmitted={isSubmitted}
                  />
                )}
                {nameField && (
                  <>
                    {t("or")}
                    <FindingSupplierSelectField
                      field={nameField}
                      control={control}
                      error={errors.name}
                      isSubmitted={isSubmitted}
                    />
                  </>
                )}
              </FormFieldsWrapper>
            )}
            <StyledButton
              type="submit"
              disabled={
                formState === "loading" || isLoadingFilters || !serviceType
              }
            >
              {formState === "loading" || isLoadingFilters ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <>
                  <Desktop>
                    <SearchIcon />
                  </Desktop>
                  <Mobile>{t("searchButton")}</Mobile>
                </>
              )}
            </StyledButton>
          </form>
        </StyledFormContainer>
      </StyledContainer>
      <StyledSearchResultsContainer>
        {formState === "results" && (
          <>
            <StyledResultsNumber>
              {`${preTextResult?.value} ${pagination.totalCount} ${postTextResult?.value}`}
            </StyledResultsNumber>
            <CircleImageLinkListTable
              titles={titlesArray}
              itemsList={results}
            />

            {pagination.totalPages > 1 && (
              <StyledPaginationContainer>
                <StyledPaginationButton
                  variant="ghost"
                  className="left"
                  disabled={pagination.currentPage === 0 || isLoadingResults}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                >
                  <LeftArrowIcon />
                </StyledPaginationButton>

                <StyledPaginationInfo>
                  {`${pagination.currentPage + 1} ${t("of")} ${pagination.totalPages}`}
                </StyledPaginationInfo>

                <StyledPaginationButton
                  variant="ghost"
                  className="right"
                  disabled={
                    pagination.currentPage === pagination.totalPages - 1 ||
                    isLoadingResults
                  }
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                >
                  <RightArrowIcon />
                </StyledPaginationButton>
              </StyledPaginationContainer>
            )}

            {isLoadingResults && <CircularProgress />}
          </>
        )}
      </StyledSearchResultsContainer>
    </BlockWrapper>
  )
}

export default FindingSupplier
